import React from 'react';
import { Link } from 'react-router-dom';
import '../home/Home.css';
import { SUPPORT_EMAIL, MAILTO_SUPPORT_HREF } from '../../constants/publicSite';

function LegalChrome({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--cream)]">
      <header className="border-b border-slate-200/70 bg-[var(--cream)] shadow-[0_1px_0_rgba(15,23,42,0.04)]">
        <nav
          className="landing-nav-inner flex min-h-[var(--landing-nav-height)] w-full items-center justify-between gap-4 py-3 pl-8 pr-8"
          aria-label="Main"
        >
          <div className="flex min-w-0 flex-1 items-center">
            <Link to="/" className="landing-logo min-w-0 truncate">
              parachute<span className="iq">IQ</span>
            </Link>
          </div>
          <div className="landing-nav-right flex shrink-0 items-center gap-5 sm:gap-7">
            <a href="/#pillars" className="landing-nav-link">
              Platform
            </a>
            <a href="/#built" className="landing-nav-link">
              About
            </a>
            <Link
              to="/login"
              className="landing-nav-signin touch-manipulation inline-flex items-center justify-center text-center"
            >
              Sign in
            </Link>
          </div>
        </nav>
      </header>
      <main className="mx-auto w-full max-w-[720px] flex-1 px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <article className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-10">
          <h1 className="font-['Outfit',sans-serif] text-2xl font-semibold tracking-tight text-[var(--ink)] sm:text-3xl">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-500">Last updated March 29, 2026</p>
          <div className="prose-legal mt-8 space-y-6 text-sm leading-relaxed text-gray-700">{children}</div>
          <p className="mt-10 border-t border-slate-100 pt-6 text-sm text-gray-600">
            Questions? Contact us at{' '}
            <a href={MAILTO_SUPPORT_HREF} className="font-medium text-emerald-700 underline-offset-2 hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </article>
      </main>
      <footer className="border-t border-slate-200/70 bg-[var(--cream)] py-6 text-center text-xs text-gray-500">
        <span>© {new Date().getFullYear()} ParachuteIQ</span>
        <span className="mx-2 text-gray-300" aria-hidden>
          ·
        </span>
        <Link to="/privacy" className="text-emerald-800/90 hover:underline">
          Privacy
        </Link>
        <span className="mx-2 text-gray-300" aria-hidden>
          ·
        </span>
        <Link to="/terms" className="text-emerald-800/90 hover:underline">
          Terms
        </Link>
      </footer>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalChrome title="Privacy Policy">
      <section>
        <h2 className="text-base font-semibold text-gray-900">Introduction</h2>
        <p className="mt-2">
          ParachuteIQ (“we,” “us”) provides software and related services for Section 280G analysis and related
          workflows. This Privacy Policy describes how we collect, use, and share information in connection with the
          ParachuteIQ application and website.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Information we process</h2>
        <p className="mt-2">
          Depending on how you use the service, we may process account identifiers (such as name and work email),
          authentication data, usage and technical logs (such as IP address and device/browser type), and content you
          submit within the product (for example, transaction and compensation data you choose to enter for analyses).
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">How we use information</h2>
        <p className="mt-2">
          We use information to provide and secure the service, authenticate users, operate and improve the product,
          comply with law, and communicate with you about the service. We do not sell your personal information.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Retention and security</h2>
        <p className="mt-2">
          We retain information as long as needed to provide the service and for legitimate business and legal purposes.
          We implement appropriate technical and organizational measures designed to protect information; no method of
          transmission or storage is completely secure.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Your choices and rights</h2>
        <p className="mt-2">
          Depending on your location, you may have rights to access, correct, delete, or restrict certain processing of
          your personal information. Contact us using the email below to make a request.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Changes</h2>
        <p className="mt-2">
          We may update this policy from time to time. We will post the revised version on this page and update the
          “Last updated” date above.
        </p>
      </section>
    </LegalChrome>
  );
}

export function TermsPage() {
  return (
    <LegalChrome title="Terms of Use">
      <section>
        <h2 className="text-base font-semibold text-gray-900">Agreement</h2>
        <p className="mt-2">
          These Terms of Use (“Terms”) govern your access to and use of ParachuteIQ’s website and software services. By
          accessing or using the service, you agree to these Terms on behalf of yourself or the organization you
          represent.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">The service</h2>
        <p className="mt-2">
          ParachuteIQ provides tools for Section 280G-related analysis and workflows. Features and availability may
          change. We may suspend or terminate access for operational, security, or contractual reasons.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Accounts and access</h2>
        <p className="mt-2">
          Access is provided through organization-managed or administrator-provisioned accounts. You are responsible for
          safeguarding credentials and for activity under your account. Notify us promptly of unauthorized use.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Your data</h2>
        <p className="mt-2">
          You retain rights in data you submit. You grant us the rights reasonably necessary to host, process, and
          display that data to provide the service. You represent that you have the rights needed to submit the data you
          provide.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Disclaimer</h2>
        <p className="mt-2">
          The service is provided “as is” and “as available.” ParachuteIQ is a software tool; it does not constitute
          legal, tax, or accounting advice. You are responsible for your professional judgments and compliance
          obligations.
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">Limitation of liability</h2>
        <p className="mt-2">
          To the maximum extent permitted by law, ParachuteIQ and its suppliers will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for loss of profits, data, or goodwill, arising
          from or related to the service. Our aggregate liability for any claim arising from the service is limited to
          the amount you paid us for the service in the twelve months before the claim (or, if none, one hundred U.S.
          dollars).
        </p>
      </section>
      <section>
        <h2 className="text-base font-semibold text-gray-900">General</h2>
        <p className="mt-2">
          These Terms are governed by the laws of the jurisdiction designated in your subscription or order form, or
          otherwise the State of Delaware, excluding conflict-of-law rules. If a provision is unenforceable, the
          remainder stays in effect.
        </p>
      </section>
    </LegalChrome>
  );
}
